'use client';

import React, {
  FC,
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import classNames from 'classnames';
import style from './client-layout.module.scss';

import { IUser } from '@/src/v2/service/graphql/generated/gql-api.generated';

import {
  MenuBoard,
  setMenuBoard,
  setWithAd,
} from '@/src/v2/store/slices/presentation/PresentationSlice';

import { pageView } from '@/src/v2/utils/helpers/gtag/gtag';
import { signOutService } from '@/src/services/auth';
import { setChannel, setUser } from '../../store/slices/user/UserSlice';
import { useAppDispatch } from '@/src/v2/hooks/useAppDispatch/UseAppDispatch';
import { useAppSelector } from '@/src/v2/hooks/useAppSelector/UseAppSelector';
import {
  useIsSearchOpenSelector,
  useIsSidebarOpenSelector,
  useUserSelector,
} from '@/src/v2/store/selectors';
import useOnlineStatus from '../../hooks/useCheckIsMobileView/useCheckIsMobileView'; // useCheckIsMobileView,

//import Button from '../../components/Button/Button';
import CookieConsent, { Cookies } from 'react-cookie-consent';
//import Icon from '../../components/Icon';
import 'react-loading-skeleton/dist/skeleton.css';
//import Skeleton from 'react-loading-skeleton';
import { useEventContext } from '../../page-elements/Event/context/EventContext';
//import DownloadPopup from '@/src/components/clean/DownloadPopup/DownloadPopup';
import Link from 'next/link';
import dynamic from 'next/dynamic';
const DownloadPopupDynamic = dynamic(
  () => import('@/src/components/clean/DownloadPopup/DownloadPopup'),
  {
    ssr: false,
  }
);
const CookieConsentDynamic = dynamic(() => import('react-cookie-consent'), {
  ssr: false,
});
const ButtonDynamic = dynamic(() => import('../../components/Button/Button'), {
  ssr: false,
});
const IconDynamic = dynamic(() => import('../../components/Icon'), {
  ssr: false,
});

type Props = {
  pageType: MenuBoard;
};

const ClientLayout: FC<PropsWithChildren<Props>> = ({ children, pageType }) => {
  const { streamMapDispatch, streamMap } = useEventContext();
  const isMenuOpen = useAppSelector(useIsSidebarOpenSelector);
  const isSearchOpen = useAppSelector(useIsSearchOpenSelector);
  const reduxUser = useAppSelector(useUserSelector);
  const [localUser, setLocalUser] = useState<IUser | null>(reduxUser);
  const [isAdClosed, setIsAdClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(false);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    pageView(pathname, document.title);
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel('app_channel');

    const handleMessage = (event) => {
      if (event.data === 'tab_opened') {
        console.log('Web app opened in another tab');
      } else if (
        event.data === 'user_logged_out' ||
        event.data === 'user_logged_in'
      ) {
        //window.location.reload();
      }
    };

    channel.postMessage('tab_opened');
    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, []);

  useEffect(() => {
    setLocalUser(reduxUser);
    if (reduxUser?.firstLogin === true) {
      setIsFirstLogin(true);
    }
  }, [reduxUser]);

  useEffect(() => {
    dispatch(setMenuBoard(pageType));
  }, [localUser]);

  const contentWithAd = useMemo(() => {
    if (localUser) {
      return false;
    }

    return !isAdClosed;
  }, [localUser, isAdClosed]);

  useEffect(() => {
    dispatch(setWithAd(contentWithAd));
  }, [contentWithAd]);

  useEffect(() => {
    setTimeout(() => {
      setIsShow(true);
    }, 2000);

    if (pageType !== 'event') {
      streamMapDispatch({ type: 'init', payload: { setup: null } });
    }
  }, []);

  const handleLogout = async () => {
    const channel = new BroadcastChannel('app_channel');
    setIsLoading(true);
    await signOutService(router);
    dispatch(setUser(null));
    dispatch(setChannel(null));
    channel.postMessage('user_logged_out');
    await router.push('/');
    setIsLoading(false);
  };
  const handleCookie = async (status: boolean) => {
    if (status) {
      Cookies.set('KLıC', 'true', { expires: 365 });
      setCookieAccepted(true); // Update state to reflect cookie acceptance
    }
    setIsShow(false);
  };

  // const { withAd } = useAppSelector((state) => state.presentation);
  const status = useOnlineStatus();

  return (
    <>
      <div
        className={classNames(
          style['client-layout'],
          {
            [style['client-layout--with-ad']]:
              pageType !== 'dashboard' && false,
          },
          {
            [style['client-layout--menu-open']]: isMenuOpen,
          },
          {
            [style['client-layout--menu-closed']]: !isMenuOpen,
          }
        )}
        suppressHydrationWarning={true}
      >
        {children}
      </div>
      {isShow && (
        <div className={style['Cookiebar']}>
          <CookieConsentDynamic
            location="bottom"
            enableDeclineButton={true}
            buttonText="Accept"
            cookieName="KLıC"
            declineButtonText="Decline"
            buttonClasses={'secondary-v2'}
            buttonStyle={{
              display: 'none',
            }}
            declineButtonStyle={{
              display: 'none',
            }}
            contentClasses={style['content']}
            expires={150}
            setDeclineCookie={false}
            onAccept={() => setCookieAccepted(true)}
          >
            <div className={style['content-logo']}>
              <IconDynamic icon="CookiePrferenceV1" width={95} />
            </div>
            <div className={style['content-description']}>
              <span>
                KLıC uses cookies to improve user experience and site
                performance, offer content tailored to your interests and enable
                social media sharing. By clicking "Agree" or continuing to use
                our site, you agree to our use of cookies and our{' '}
                <Link href={'privacy-policy'}>Privacy Policy</Link>.
              </span>
            </div>
            <div className={style['content--buttons']}>
              <ButtonDynamic
                className=""
                onClick={() => handleCookie(true)}
                label="Accept"
                type="primary-v2"
              />
              <ButtonDynamic
                className=""
                onClick={() => handleCookie(false)}
                label="Decline"
                type="secondary-v2"
              />
            </div>
          </CookieConsentDynamic>
        </div>
      )}
      {!status && (
        <div
          className={classNames(style['offline-service'], {
            [style['offline-service--menu-closed']]:
              pageType === 'dashboard' || !isMenuOpen,
          })}
        >
          <div className={style['offline-service--content']}>
            <IconDynamic icon="rocket" width={48} />
            <div>
              Connection Lost. Please check your Internet and try again.
            </div>
          </div>
        </div>
      )}
      {reduxUser && reduxUser?.isLock && (
        <div className={style['offline-service']}>
          <div className={style['offline-service--content']}>
            <IconDynamic icon="bluelock" width={48} />
            <div>
              Your account has been deactivated by an administrator.
              <br /> Please reach out to our technical support for more details.
            </div>
            <ButtonDynamic
              loading={isLoading}
              className="button_mt-10"
              label="Logout"
              type="secondary-v2"
              icon="logout"
              icon_height={28}
              icon_width={28}
              onClick={() => handleLogout()}
            />
          </div>
        </div>
      )}
      {reduxUser && reduxUser?.isTempLock && reduxUser?.isLock === false && (
        <div className={style['offline-service']}>
          <div className={style['offline-service--content']}>
            <IconDynamic icon="stopIcon" width={48} />
            <div>
              Your account has been temporarily locked.
              <br /> Please reach out to our technical support for more details.
            </div>
            <ButtonDynamic
              loading={isLoading}
              className="button_mt-10"
              label="Logout"
              type="secondary-v2"
              icon="logout"
              icon_height={28}
              icon_width={28}
              onClick={() => handleLogout()}
            />
          </div>
        </div>
      )}
      {reduxUser &&
        (reduxUser.id || reduxUser.email) &&
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('KLIC-APP-POPUP') !== 'true' && (
          // <DownloadPopupDynamic />
          <></>
        )}
    </>
  );
};

export default ClientLayout;
